import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Music, Calendar, DollarSign, User, ExternalLink, Edit, Plus, Search, RefreshCw, FilePlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { EditReleaseModal } from '../components/EditReleaseModal';
import { CalendarSyncService } from '../services/calendarSyncService';

interface Release {
  id: string;
  name: string;
  artist_id: string;
  release_date: string;
  status: string;
  ad_budget: number;
  total_budget: number;
  service_package: string;
  is_new_customer: boolean;
  agency_fee: number;
  is_published: boolean;
  spotify_url: string | null;
  created_at: string;
  meta_campaign_id?: string | null;
  meta_campaign_name?: string | null;
  artists?: {
    name: string;
  };
}

const Releases: React.FC = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [creatingInvoice, setCreatingInvoice] = useState<string | null>(null);

  useEffect(() => {
    loadReleases();
    loadLastSyncTime();
  }, []);

  const loadLastSyncTime = () => {
    const stored = localStorage.getItem('lastCalendarSync');
    if (stored) {
      setLastSyncTime(stored);
    }
  };

  const handleCalendarSync = async () => {
    setSyncing(true);
    try {
      const result = await CalendarSyncService.syncReleases();
      
      if (result.success) {
        const now = new Date().toISOString();
        setLastSyncTime(now);
        localStorage.setItem('lastCalendarSync', now);
        
        toast.success(
          `Sync erfolgreich: ${result.created} erstellt, ${result.updated} aktualisiert, ${result.skipped} übersprungen`,
          { duration: 5000 }
        );
      } else {
        toast.error(`Sync fehlgeschlagen: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Calendar sync error:', error);
      toast.error('Fehler beim Synchronisieren mit Google Calendar');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateInvoice = async (releaseId: string) => {
    console.log('📝 Creating invoice for release:', releaseId);
    setCreatingInvoice(releaseId);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      console.log('🔑 Supabase URL:', supabaseUrl);
      console.log('🔑 Supabase Anon Key:', supabaseAnonKey ? 'exists' : 'missing');
      
      const response = await fetch(`${supabaseUrl}/functions/v1/create-lexoffice-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ release_id: releaseId })
      });

      console.log('📤 Response status:', response.status);
      
      const data = await response.json();
      console.log('📤 Response data:', data);

      if (response.ok) {
        if (data.already_exists) {
          toast.success(`Rechnung existiert bereits: ${data.invoice_id}`);
        } else {
          toast.success(`Rechnung erstellt: ${data.invoice_id}`);
        }
      } else {
        toast.error(`Fehler: ${data.error || data.details}`);
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Fehler beim Erstellen der Rechnung');
    } finally {
      setCreatingInvoice(null);
    }
  };

  const loadReleases = async () => {
    try {
      const { data, error } = await supabase
        .from('releases')
        .select(`
          *,
          artists (
            name,
            facebook_page_id
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setReleases((data as any) || []);
    } catch (error) {
      console.error('Error loading releases:', error);
      toast.error('Fehler beim Laden der Releases');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'approved':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'in_progress':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'Eingereicht';
      case 'approved':
        return 'Genehmigt';
      case 'in_progress':
        return 'In Bearbeitung';
      case 'completed':
        return 'Abgeschlossen';
      case 'cancelled':
        return 'Abgebrochen';
      default:
        return status;
    }
  };

  const getPackageLabel = (pkg: string) => {
    switch (pkg) {
      case 'ad_only':
        return 'Ads only';
      case 'content_only':
        return 'Content only';
      case 'full':
        return 'Full';
      default:
        return pkg;
    }
  };

  const filteredReleases = releases.filter(release => {
    const matchesFilter = filter === 'all' || release.status === filter;
    const matchesSearch = searchQuery === '' || 
      release.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      release.artists?.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white">Lade Releases...</div>
      </div>
    );
  }

  return (
    <>
      {editingRelease && (
        <EditReleaseModal
          release={editingRelease}
          onClose={() => setEditingRelease(null)}
          onUpdate={() => {
            loadReleases();
            setEditingRelease(null);
          }}
        />
      )}
      
      <div className="min-h-screen bg-black p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-3xl font-bold text-white">Releases</h1>
              <p className="text-gray-400">Übersicht aller eingereichten Kampagnen</p>
              {lastSyncTime && (
                <p className="text-xs text-gray-500 mt-1">
                  Letzter Kalender-Sync: {new Date(lastSyncTime).toLocaleString('de-DE')}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCalendarSync}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                title="Releases mit Google Calendar synchronisieren"
              >
                <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Calendar Sync'}
              </button>
              <Link
                to="/new-campaign"
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Release
              </Link>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6 flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Alle ({releases.length})
            </button>
            <button
              onClick={() => setFilter('submitted')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'submitted'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Eingereicht ({releases.filter(r => r.status === 'submitted').length})
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'approved'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Genehmigt ({releases.filter(r => r.status === 'approved').length})
            </button>
            <button
              onClick={() => setFilter('in_progress')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'in_progress'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              In Bearbeitung ({releases.filter(r => r.status === 'in_progress').length})
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Abgeschlossen ({releases.filter(r => r.status === 'completed').length})
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Suche nach Release-Name oder Künstler..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Releases Table */}
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Release
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Künstler
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Release-Datum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Paket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Budget
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Eingereicht
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredReleases.map((release) => (
                    <tr key={release.id} className="hover:bg-gray-800/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Music className="w-4 h-4 text-gray-500 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-white">{release.name}</div>
                            <div className="text-xs text-gray-500">
                              {release.is_published ? 'Veröffentlicht' : 'Unveröffentlicht'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-300">
                          <User className="w-4 h-4 mr-2 text-gray-500" />
                          {release.artists?.name || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-300">
                          <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                          {new Date(release.release_date).toLocaleDateString('de-DE')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            release.status
                          )}`}
                        >
                          {getStatusLabel(release.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {getPackageLabel(release.service_package)}{release.is_new_customer ? ' (Neukunde)' : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-300">
                          <DollarSign className="w-4 h-4 mr-1 text-gray-500" />
                          {(release.ad_budget / 100)?.toLocaleString('de-DE')}€
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {new Date(release.created_at).toLocaleDateString('de-DE')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          {release.meta_campaign_id && (
                            <a
                              href={`https://business.facebook.com/adsmanager/manage/campaigns?act=626419741407621&selected_campaign_ids=${release.meta_campaign_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center gap-1"
                              title={release.meta_campaign_name || 'Meta Campaign'}
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02Z"/>
                              </svg>
                              Meta
                            </a>
                          )}
                          {release.spotify_url && (
                            <a
                              href={release.spotify_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center gap-1"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Spotify
                            </a>
                          )}
                          <button
                            onClick={() => handleCreateInvoice(release.id)}
                            disabled={creatingInvoice === release.id}
                            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors flex items-center gap-1"
                            title="Rechnung erstellen"
                          >
                            <FilePlus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingRelease(release)}
                            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors flex items-center gap-1"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Empty State */}
          {filteredReleases.length === 0 && (
            <div className="text-center py-12">
              <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">Keine Releases gefunden</h3>
              <p className="text-gray-500">
                {filter === 'all'
                  ? 'Es wurden noch keine Releases eingereicht.'
                  : `Keine Releases mit Status "${getStatusLabel(filter)}".`}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Releases;
